import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import Modal from '../../../core/components/Modal.tsx';
import Button from '../../../core/components/Button.tsx';
import FormField from '../../../core/components/FormField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import { GROUP_SEPARATOR } from '../../../document/pages/Assistance/useDocumentConfig';

const FORM_DEFAULT_VALUES = {
    tableDataLabelName: '',
    tableFieldLabelName: '',
};

interface ItemTypeFormValues {
    tableDataLabelName: string;
    tableFieldLabelName: string;
}

interface UniversalItemTypeFormModalProps {
    title: string;
    submitButtonLabel: string;
    onClose: () => void;
    onSubmit: (formData: ItemTypeFormValues) => void;
    // a saved table: its output name can't change any more
    data?: ItemTypeFormValues;
    // the output names of the header fields and tables, which a new table can't take
    takenNames?: string[];
}

const UniversalItemTypeFormModal = ({
    title,
    onClose,
    submitButtonLabel,
    onSubmit,
    data = undefined,
    takenNames = [],
}: UniversalItemTypeFormModalProps) => {
    const { t } = useTranslation('config');

    const form = useForm({ defaultValues: data ?? FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    const validateOutputName = (value: string) => {
        if (data) return true;
        if (value.includes(GROUP_SEPARATOR)) return t('configPage.document.form.outputNameWithSeparator');
        // the backend groups the header fields under "header"
        if (value === 'header') return t('configPage.document.form.tableDataLabelNameReserved');
        if (takenNames.includes(value)) return t('configPage.document.form.duplicatedField');
        return true;
    };

    return (
        <Modal
            visible={true}
            onClose={onClose}
            title={title}
            buttons={
                <>
                    <Button type="button" onClick={onClose}>
                        {t('configPage.document.universal.modal.cancelButton')}
                    </Button>
                    <Button variant="primary" type="submit" form="add-item-type-form">
                        {submitButtonLabel}
                    </Button>
                </>
            }
        >
            <form className="flex flex-col gap-8" onSubmit={form.handleSubmit(onSubmit)} id="add-item-type-form">
                <Controller
                    name="tableDataLabelName"
                    control={form.control}
                    rules={{
                        required: t('configPage.document.form.tableDataLabelNameRequired'),
                        validate: validateOutputName,
                    }}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.tableDataLabelName')}
                            helpText={t('configPage.document.form.tableDataLabelNameHelpText')}
                            required={true}
                            error={formErrors.tableDataLabelName}
                        >
                            <StringField
                                required={true}
                                {...field}
                                onChange={(event) => field.onChange(event.target.value.replace(/ /g, '_'))}
                                disabled={!!data}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="tableFieldLabelName"
                    control={form.control}
                    rules={{ required: t('configPage.document.form.tableFieldLabelNameRequired') }}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.tableFieldLabelName')}
                            helpText={t('configPage.document.form.tableFieldLabelNameHelpText')}
                            required={true}
                            error={formErrors.tableFieldLabelName}
                        >
                            <StringField required={true} {...field} />
                        </FormField>
                    )}
                />
            </form>
        </Modal>
    );
};

export default UniversalItemTypeFormModal;
